/**
 * GUS Data Schemas
 *
 * TypeScript definitions for all GeoJSON data schemas used in the GUS v2 platform.
 * These schemas match the actual data structures stored in the spatial database.
 *
 * Data Categories:
 * 1. Citizen Satisfaction - Hexagon-based satisfaction scores by topic
 * 2. Demographics & Employment - Population and job statistics
 * 3. Points of Interest & Activity - Location-based POI and activity data
 * 4. Urban Spaces - Classified urban features and land use
 * 5. Mobility Data - Origin-destination matrices and traffic patterns
 */

// ============================================================================
// 1. CITIZEN SATISFACTION DATA
// ============================================================================

/**
 * Citizen Satisfaction (Topics_HEX8.geojson / Topics_HEX9.geojson)
 *
 * Hexagon-based citizen satisfaction scores across multiple urban topics.
 * Each hexagon represents aggregated satisfaction data for a geographic area.
 */
export interface CitizenSatisfactionProperties {
  /** Hexagon ID (H3 index) - Resolution 8 or 9 */
  HEX8?: string;
  HEX9?: string;

  /** Neighborhood name */
  NEIGHBORHENAME?: string;

  /** Topic category (e.g., "Parks", "Transportation", "Safety") */
  TOPIC: string;

  /** Publication year of the survey data */
  pub_year: number;

  /** Total number of responses for this hexagon/topic */
  total: number;

  /** Overall satisfaction score (0-100 scale) */
  overall_score: number;

  /** Breakdown of responses by satisfaction level */
  very_satisfied?: number;
  satisfied?: number;
  neutral?: number;
  dissatisfied?: number;
  very_dissatisfied?: number;

  /** Additional metadata */
  survey_id?: string;
  data_quality?: 'high' | 'medium' | 'low';
}

export interface CitizenSatisfactionFeature {
  type: 'Feature';
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  properties: CitizenSatisfactionProperties;
}

export interface CitizenSatisfactionCollection {
  type: 'FeatureCollection';
  features: CitizenSatisfactionFeature[];
}

// ============================================================================
// 2. DEMOGRAPHICS & EMPLOYMENT DATA
// ============================================================================

/**
 * Demographics & Employment (population_and_employment_HEX8.geojson)
 *
 * Population statistics, employment data, and floor area metrics
 * aggregated by hexagon.
 */
export interface DemographicsProperties {
  /** Hexagon ID (H3 index) */
  HEX: string;

  /** Neighborhood name */
  NEIGHBORHE?: string;

  /** Population count (2022 or latest census) */
  POP22_COUN?: number;

  /** Total Gross Floor Area (GFA) in square meters */
  GFAtotal?: number;

  /** Number of jobs/employment positions */
  empJOB?: number;

  /** Population density (people per sq km) */
  pop_density?: number;

  /** Job density (jobs per sq km) */
  job_density?: number;

  /** Demographic breakdowns */
  age_0_14?: number;
  age_15_64?: number;
  age_65_plus?: number;

  /** Gender distribution */
  male_count?: number;
  female_count?: number;

  /** Household statistics */
  households?: number;
  avg_household_size?: number;

  /** Economic indicators */
  median_income?: number;
  unemployment_rate?: number;
}

export interface DemographicsFeature {
  type: 'Feature';
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  properties: DemographicsProperties;
}

export interface DemographicsCollection {
  type: 'FeatureCollection';
  features: DemographicsFeature[];
}

// ============================================================================
// 3. POINTS OF INTEREST & ACTIVITY DATA
// ============================================================================

/**
 * POI & Activity Data (riyadh_activity_googlemaps.geojson)
 *
 * Point-based data for places of interest, businesses, and urban activities.
 * Sourced from Google Maps and other activity datasets.
 */
export interface POIActivityProperties {
  /** Unique post/place identifier */
  post_id: string;

  /** Timestamp of data collection or last update */
  timestamp?: string;

  /** Primary space type classification */
  primary_space_type?: string;

  /** List of activities available at this location */
  activities?: string[];

  /** Available amenities */
  amenities?: string[];

  /** Place name */
  name?: string;

  /** Business/place category */
  category?: string;

  /** Rating (0-5 scale) */
  rating?: number;

  /** Number of reviews */
  review_count?: number;

  /** Operating hours */
  opening_hours?: string;

  /** Contact information */
  phone?: string;
  website?: string;

  /** Accessibility features */
  wheelchair_accessible?: boolean;
  parking_available?: boolean;

  /** Popularity metrics */
  popularity_score?: number;
  peak_hours?: string[];
}

export interface POIActivityFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: number[]; // [longitude, latitude]
  };
  properties: POIActivityProperties;
}

export interface POIActivityCollection {
  type: 'FeatureCollection';
  features: POIActivityFeature[];
}

// ============================================================================
// 4. URBAN SPACES DATA
// ============================================================================

/**
 * Urban Spaces (urban_spaces.geojson)
 *
 * Classified urban features including parks, plazas, sports facilities,
 * and other public spaces.
 */
export interface UrbanSpacesProperties {
  /** Space identifier */
  space_id: string;

  /** Space name */
  name?: string;

  /** Space classification */
  space_type: 'park' | 'plaza' | 'sports' | 'playground' | 'garden' | 'waterfront' | 'other';

  /** Area in square meters */
  area_sqm?: number;

  /** Neighborhood */
  neighborhood?: string;

  /** Ownership type */
  ownership?: 'public' | 'private' | 'mixed';

  /** Accessibility */
  public_access?: boolean;
  access_hours?: string;

  /** Features and facilities */
  features?: string[];

  /** Maintenance status */
  maintenance_status?: 'excellent' | 'good' | 'fair' | 'poor';

  /** Usage metrics */
  avg_daily_visitors?: number;
  peak_season?: string;

  /** Environmental data */
  green_coverage_percent?: number;
  tree_count?: number;

  /** Last inspection/update date */
  last_updated?: string;
}

export interface UrbanSpacesFeature {
  type: 'Feature';
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
  properties: UrbanSpacesProperties;
}

export interface UrbanSpacesCollection {
  type: 'FeatureCollection';
  features: UrbanSpacesFeature[];
}

// ============================================================================
// 5. MOBILITY DATA
// ============================================================================

/**
 * Mobility Data (TomTom OD Matrices)
 *
 * Origin-Destination matrices showing movement patterns and traffic flows
 * between different zones.
 */
export interface MobilityDataProperties {
  /** Origin zone ID */
  origin_zone: string;

  /** Destination zone ID */
  destination_zone: string;

  /** Trip count for this OD pair */
  trip_count: number;

  /** Average travel time (minutes) */
  avg_travel_time?: number;

  /** Average distance (kilometers) */
  avg_distance_km?: number;

  /** Time period */
  time_period?: 'morning_peak' | 'midday' | 'evening_peak' | 'night' | 'all_day';

  /** Day type */
  day_type?: 'weekday' | 'weekend' | 'holiday';

  /** Transport mode distribution */
  mode_car_percent?: number;
  mode_public_transit_percent?: number;
  mode_walk_percent?: number;
  mode_bike_percent?: number;

  /** Traffic metrics */
  congestion_level?: 'free_flow' | 'light' | 'moderate' | 'heavy' | 'severe';
  avg_speed_kmh?: number;

  /** Data timestamp */
  data_date?: string;
}

export interface MobilityDataFeature {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: number[][];
  };
  properties: MobilityDataProperties;
}

export interface MobilityDataCollection {
  type: 'FeatureCollection';
  features: MobilityDataFeature[];
}

// ============================================================================
// UNION TYPES & HELPERS
// ============================================================================

/**
 * Union of all GUS data property types
 */
export type GUSDataProperties =
  | CitizenSatisfactionProperties
  | DemographicsProperties
  | POIActivityProperties
  | UrbanSpacesProperties
  | MobilityDataProperties;

/**
 * Union of all GUS feature types
 */
export type GUSFeature =
  | CitizenSatisfactionFeature
  | DemographicsFeature
  | POIActivityFeature
  | UrbanSpacesFeature
  | MobilityDataFeature;

/**
 * Union of all GUS collection types
 */
export type GUSCollection =
  | CitizenSatisfactionCollection
  | DemographicsCollection
  | POIActivityCollection
  | UrbanSpacesCollection
  | MobilityDataCollection;

/**
 * Data type discriminator for runtime type checking
 */
export enum GUSDataType {
  CITIZEN_SATISFACTION = 'citizen_satisfaction',
  DEMOGRAPHICS = 'demographics',
  POI_ACTIVITY = 'poi_activity',
  URBAN_SPACES = 'urban_spaces',
  MOBILITY = 'mobility'
}

/**
 * Upload metadata for data validation and processing
 */
export interface GUSUploadMetadata {
  dataType: GUSDataType;
  fileName: string;
  fileSize: number;
  featureCount?: number;
  boundingBox?: number[]; // [minLon, minLat, maxLon, maxLat]
  uploadedBy: string;
  uploadedAt: string;
  validated: boolean;
  validationErrors?: string[];
}

/**
 * Type guard to check if properties match CitizenSatisfactionProperties
 */
export function isCitizenSatisfactionData(props: GUSDataProperties): props is CitizenSatisfactionProperties {
  return 'TOPIC' in props && 'overall_score' in props;
}

/**
 * Type guard to check if properties match DemographicsProperties
 */
export function isDemographicsData(props: GUSDataProperties): props is DemographicsProperties {
  return 'POP22_COUN' in props || 'empJOB' in props;
}

/**
 * Type guard to check if properties match POIActivityProperties
 */
export function isPOIActivityData(props: GUSDataProperties): props is POIActivityProperties {
  return 'post_id' in props && 'primary_space_type' in props;
}

/**
 * Type guard to check if properties match UrbanSpacesProperties
 */
export function isUrbanSpacesData(props: GUSDataProperties): props is UrbanSpacesProperties {
  return 'space_id' in props && 'space_type' in props;
}

/**
 * Type guard to check if properties match MobilityDataProperties
 */
export function isMobilityData(props: GUSDataProperties): props is MobilityDataProperties {
  return 'origin_zone' in props && 'destination_zone' in props;
}
